import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { imgSrc } from '../lib/imgSrc';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const EditMachineryDialog = ({ open, onClose, machinery, onSuccess }) => {
  const [formData, setFormData] = useState({
    plant_location_id: '',
    component: '',
    location: '',
    manufacturer: '',
    model: '',
    serial_number: '',
    status: 'operational',
    notes: ''
  });
  const [locations, setLocations] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // Fetch locations
  useEffect(() => {
    if (open) {
      axios.get(`${API}/locations`).then(res => {
        setLocations(res.data);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (machinery) {
      setFormData({
        plant_location_id: machinery.plant_location_id || '',
        component: machinery.component || '',
        location: machinery.location || '',
        manufacturer: machinery.manufacturer || '',
        model: machinery.model || '',
        serial_number: machinery.serial_number || '',
        status: machinery.status || 'operational',
        notes: machinery.notes || ''
      });
      setImagePreview(machinery.main_image);
    }
  }, [machinery]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imageUrl = imagePreview;
      if (imageFile) {
        const formDataImage = new FormData();
        formDataImage.append('file', imageFile);
        const uploadRes = await axios.post(`${API}/upload-image`, formDataImage);
        imageUrl = uploadRes.data.image_url;
      }

      const submitData = {
        ...formData,
        plant_location_id: formData.plant_location_id, // Empty string will be converted to null on backend
        main_image: imageUrl,
      };

      await axios.put(`${API}/machinery/${machinery.id}`, submitData);
      toast.success('Equipment updated successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update equipment');
    } finally {
      setUploading(false);
    }
  };

  // Find current location name for display
  const getCurrentLocationName = () => {
    if (!formData.plant_location_id) return 'Unassigned';
    const loc = locations.find(l => l.id === formData.plant_location_id);
    return loc ? loc.name : 'Unassigned';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Equipment</DialogTitle>
          <DialogDescription>Update equipment details and settings</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Plant Location Selector */}
          <div className="space-y-2">
            <Label htmlFor="edit-plant-location">Plant Location</Label>
            <select
              id="edit-plant-location"
              data-testid="edit-plant-location-select"
              value={formData.plant_location_id}
              onChange={(e) => setFormData({ ...formData, plant_location_id: e.target.value })}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="">Unassigned</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              Current: {getCurrentLocationName()}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-component">Component *</Label>
              <Input
                id="edit-component"
                data-testid="edit-component-input"
                value={formData.component}
                onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-location">Local Location *</Label>
              <Input
                id="edit-location"
                data-testid="edit-location-input"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="e.g., Building A, Room 101"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit-manufacturer">Manufacturer *</Label>
              <Input
                id="edit-manufacturer"
                data-testid="edit-manufacturer-input"
                value={formData.manufacturer}
                onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-model">Model *</Label>
              <Input
                id="edit-model"
                data-testid="edit-model-input"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-serial">Serial Number *</Label>
            <Input
              id="edit-serial"
              data-testid="edit-serial-input"
              value={formData.serial_number}
              onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-status">Status</Label>
            <select
              id="edit-status"
              data-testid="edit-status-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="operational">Operational</option>
              <option value="maintenance_needed">Maintenance Needed</option>
              <option value="down">Down</option>
              <option value="lockout">Lockout</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-image">Main Image</Label>
            <Input
              id="edit-image"
              data-testid="edit-image-input"
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="mt-2">
                <img src={imgSrc(imagePreview)} alt="Preview" className="max-h-40 rounded" />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-notes">Notes</Label>
            <Textarea
              id="edit-notes"
              data-testid="edit-notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading} data-testid="update-machinery-btn">
              {uploading ? 'Updating...' : 'Update Equipment'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMachineryDialog;
